import { ArrowDown, ArrowUp, Plus, RefreshCw, Sparkles, X } from "lucide-react"
import { useEffect, useState } from "react"
import { cn } from "../../utils/cn"

type Preset = "fast" | "smart" | "smart_plus"
interface Prompt {
	label: string
	query: string
	matches?: number
}
interface Chip {
	label: string
	query: string
	category?: string
}

const PRESETS: Array<{ id: Preset; name: string; description: string }> = [
	{ id: "fast", name: "Fast", description: "Today's search on the shopper's words. No AI." },
	{
		id: "smart",
		name: "Smart",
		description: "AI turns requests into filtered searches. Shoppers can tap “Sort by best fit” to rerank.",
	},
	{ id: "smart_plus", name: "Smart+", description: "Smart, and every result list is reranked with a reason per product." },
]

export function SmartSearchSettings({
	organizationId,
	getAuthHeader,
	activePlaceholders,
	onPlaceholdersChanged,
}: {
	organizationId: string
	getAuthHeader: () => Promise<Record<string, string>>
	activePlaceholders: string[]
	onPlaceholdersChanged: () => void
}) {
	const base = `${import.meta.env.VITE_BACKEND_URL || ""}/api/v1/search`
	const [preset, setPreset] = useState<Preset>("smart_plus")
	const [enabled, setEnabled] = useState(true)
	const [shortlistV2, setShortlistV2] = useState(false)
	const [chips, setChips] = useState<Chip[]>([])
	const [placeholders, setPlaceholders] = useState<string[]>([])
	const [activeChips, setActiveChips] = useState<Chip[]>([])
	const [prompts, setPrompts] = useState<Prompt[]>([])
	const [activePrompts, setActivePrompts] = useState<Prompt[]>([])
	const [ownLabel, setOwnLabel] = useState("")
	const [ownQuery, setOwnQuery] = useState("")
	// The latest saved list, so a quick second tap builds on the first save rather than a stale prop.
	const [savedPlaceholders, setSavedPlaceholders] = useState(activePlaceholders)
	useEffect(() => setSavedPlaceholders(activePlaceholders), [activePlaceholders])
	const [busy, setBusy] = useState("")
	const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null)

	async function request(path: string, method = "GET", body?: unknown) {
		const response = await fetch(`${base}${path}`, {
			method,
			headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
			...(body ? { body: JSON.stringify(body) } : {}),
		})
		const result = await response.json()
		if (!response.ok) throw new Error(result.error || "Request failed")
		return result
	}

	const loadActiveChips = async () => {
		const result = await request(`/collections/products/config/intent-chips?organizationId=${organizationId}`)
		setActiveChips(result.intentChips || [])
	}

	useEffect(() => {
		let stopped = false
		const load = async () => {
			try {
				const [pipeline, pool, cards] = await Promise.all([
					request(`/config/search-pipeline?organizationId=${organizationId}`),
					request("/config/suggestions"),
					request(`/config/smart-prompts?organizationId=${organizationId}`),
				])
				if (stopped) return
				setPreset(pipeline.preset)
				setEnabled(pipeline.enabled)
				setShortlistV2(pipeline.shortlist_v2 === true)
				setChips(pool.chips || [])
				setPrompts(pool.prompts || [])
				setPlaceholders(pool.placeholders || [])
				setActivePrompts(cards.prompts || [])
				await loadActiveChips()
			} catch (error: any) {
				if (!stopped) setMessage({ error: true, text: error.message })
			}
		}
		void load()
		return () => {
			stopped = true
		}
	}, [organizationId])

	const run = async (label: string, fn: () => Promise<string>) => {
		setBusy(label)
		setMessage(null)
		try {
			setMessage({ error: false, text: await fn() })
		} catch (error: any) {
			setMessage({ error: true, text: error.message })
		} finally {
			setBusy("")
		}
	}

	const savePipeline = (update: { preset?: Preset; enabled?: boolean; shortlist_v2?: boolean }) =>
		run("pipeline", async () => {
			const result = await request("/config/search-pipeline", "PUT", update)
			setPreset(result.preset)
			setEnabled(result.enabled)
			setShortlistV2(result.shortlist_v2 === true)
			return update.shortlist_v2 === undefined ? "Search mode saved" : "Widget version saved"
		})

	const generate = () =>
		run("generate", async () => {
			const pool = await request("/config/suggestions/generate", "POST", {})
			setChips(pool.chips || [])
			setPrompts(pool.prompts || [])
			setPlaceholders(pool.placeholders || [])
			return "New suggestions added below. Nothing in your widget changed."
		})

	const addChip = (chip: Chip) =>
		run(`chip:${chip.label}`, async () => {
			const { label, query, category } = chip
			await request("/collections/products/config/intent-chips", "PUT", {
				intentChips: [...activeChips, { label, query, ...(category ? { category } : {}) }],
			})
			await loadActiveChips()
			return `Added the “${label}” chip`
		})

	const addPlaceholder = (text: string) =>
		run(`placeholder:${text}`, async () => {
			const result = await request("/config/search-placeholders", "PUT", { placeholders: [...savedPlaceholders, text] })
			setSavedPlaceholders(result.placeholders || [...savedPlaceholders, text])
			onPlaceholdersChanged()
			return "Placeholder added"
		})

	// Every change saves at once: the widget shows the cards in this order and plays the first one on opening.
	// The server answers each card ahead of time, so shoppers' taps are instant.
	const savePrompts = (key: string, next: Prompt[], done: string, after?: () => void) =>
		run(key, async () => {
			const result = await request("/config/smart-prompts", "PUT", { prompts: next })
			setActivePrompts(result.prompts || next)
			after?.()
			return `${done} · saved. Answers are being prepared, so the cards are instant in the widget.`
		})
	const movePrompt = (index: number, by: number) => {
		const next = [...activePrompts]
		const [moved] = next.splice(index, 1)
		next.splice(index + by, 0, moved)
		return savePrompts(`move:${moved.query}`, next, index + by === 0 ? `“${moved.label}” now plays first` : "Order")
	}
	const addOwnPrompt = () => {
		const query = ownQuery.trim()
		const label = ownLabel.trim() || query.split(/\s+/).slice(0, 4).join(" ")
		if (!query) return
		if (isActivePrompt({ label, query })) return setMessage({ error: true, text: "That prompt is already in use" })
		return savePrompts("own", [...activePrompts, { label, query }].slice(0, 12), `Added “${label}”`, () => {
			setOwnLabel("")
			setOwnQuery("")
		})
	}

	const togglePrompt = (prompt: Prompt) => {
		const on = isActivePrompt(prompt)
		const next = on
			? activePrompts.filter((p) => p.query.toLowerCase() !== prompt.query.toLowerCase())
			: [...activePrompts, prompt].slice(0, 12)
		return savePrompts(`prompt:${prompt.query}`, next, on ? `Removed “${prompt.label}”` : `Added “${prompt.label}”`)
	}

	const isActiveChip = (chip: Chip) => activeChips.some((c) => c.label.toLowerCase() === chip.label.toLowerCase())
	const isActivePlaceholder = (text: string) => savedPlaceholders.some((p) => p.toLowerCase() === text.toLowerCase())
	const isActivePrompt = (prompt: Prompt) => activePrompts.some((p) => p.query.toLowerCase() === prompt.query.toLowerCase())
	const group = "text-xs font-medium uppercase tracking-wide text-muted-foreground"

	return (
		<section className="bg-card border border-border rounded-lg p-6 mb-6">
			<div className="flex items-center justify-between mb-1">
				<h2 className="text-lg font-semibold">Smart search</h2>
				<label className="flex items-center gap-2 text-sm">
					<input
						checked={enabled}
						disabled={busy === "pipeline"}
						onChange={(e) => savePipeline({ enabled: e.target.checked })}
						type="checkbox"
					/>
					On
				</label>
			</div>
			<p className="text-sm text-muted-foreground mb-4">
				How the search widget answers natural-language requests. Typing suggestions always stay instant.
			</p>
			<div className={cn("grid gap-2 sm:grid-cols-3", !enabled && "opacity-50")}>
				{PRESETS.map((p) => (
					<button
						className={cn(
							"text-left border rounded-md p-3 transition-colors",
							preset === p.id ? "border-primary bg-primary/5" : "border-border hover:bg-accent",
						)}
						disabled={!enabled || busy === "pipeline"}
						key={p.id}
						onClick={() => savePipeline({ preset: p.id })}>
						<div className="font-medium text-sm">{p.name}</div>
						<div className="text-xs text-muted-foreground mt-1">{p.description}</div>
					</button>
				))}
			</div>
			{!enabled && <p className="text-xs text-muted-foreground mt-2">Off: shoppers get today's search only.</p>}

			<label className="flex items-start gap-2 text-sm mt-4">
				<input
					checked={shortlistV2}
					className="mt-1"
					disabled={busy === "pipeline"}
					onChange={(e) => savePipeline({ shortlist_v2: e.target.checked })}
					type="checkbox"
				/>
				<span>
					<b className="font-medium">Shortlist widget (UI v2)</b>
					<span className="block text-xs text-muted-foreground">
						Shoppers get the new widget with saved shortlists, sharing and comments. Off keeps today's widget.
					</span>
				</span>
			</label>

			<div className="mt-6 mb-2">
				<h3 className="font-medium">Prompt cards in your widget</h3>
				<p className="text-xs text-muted-foreground">
					Shoppers see these when they open search, in this order. The first one plays by itself as an AI search.
					Write full requests, the way a shopper would ask a friend. Changes save straight away.
				</p>
			</div>
			<div className="flex flex-col gap-1 mb-3">
				{activePrompts.length === 0 && (
					<p className="text-sm text-muted-foreground">None yet. Write one below or add a suggestion.</p>
				)}
				{activePrompts.map((prompt, i) => (
					<div className="flex items-center gap-2 px-3 py-2 rounded border border-border text-sm" key={prompt.query}>
						<span className="text-xs text-muted-foreground w-4 shrink-0">{i + 1}</span>
						<span className="flex-1">
							<b className="font-medium">{prompt.label}</b> · {prompt.query}
							{i === 0 && <span className="ml-2 text-xs text-primary">Plays first</span>}
						</span>
						<button
							aria-label={`Move “${prompt.label}” up`}
							className="p-1 rounded hover:bg-accent disabled:opacity-30"
							disabled={!!busy || i === 0}
							onClick={() => movePrompt(i, -1)}>
							<ArrowUp className="w-3.5 h-3.5" />
						</button>
						<button
							aria-label={`Move “${prompt.label}” down`}
							className="p-1 rounded hover:bg-accent disabled:opacity-30"
							disabled={!!busy || i === activePrompts.length - 1}
							onClick={() => movePrompt(i, 1)}>
							<ArrowDown className="w-3.5 h-3.5" />
						</button>
						<button
							aria-label={`Remove “${prompt.label}”`}
							className="p-1 rounded hover:bg-accent disabled:opacity-30"
							disabled={!!busy}
							onClick={() => togglePrompt(prompt)}>
							<X className="w-3.5 h-3.5" />
						</button>
					</div>
				))}
			</div>
			<form
				className="flex flex-col sm:flex-row gap-2 mb-2"
				onSubmit={(e) => {
					e.preventDefault()
					void addOwnPrompt()
				}}>
				<input
					aria-label="Card title"
					className="sm:w-44 px-3 py-2 rounded border border-border bg-transparent text-sm"
					maxLength={60}
					onChange={(e) => setOwnLabel(e.target.value)}
					placeholder="Card title, e.g. Compliment magnets"
					value={ownLabel}
				/>
				<input
					aria-label="Full request"
					className="flex-1 px-3 py-2 rounded border border-border bg-transparent text-sm"
					maxLength={200}
					onChange={(e) => setOwnQuery(e.target.value)}
					placeholder="Full request, e.g. something that gets me compliments at a friend's wedding, not pink"
					value={ownQuery}
				/>
				<button
					className="px-3 py-2 border border-border rounded-md hover:bg-accent disabled:opacity-50 flex items-center gap-1 text-sm"
					disabled={!!busy || !ownQuery.trim() || activePrompts.length >= 12}
					type="submit">
					<Plus className="w-4 h-4" /> Add prompt
				</button>
			</form>

			<div className="flex items-center justify-between mt-6 mb-2">
				<div>
					<h3 className="font-medium flex items-center gap-2">
						<Sparkles className="w-4 h-4" /> Suggested searches
					</h3>
					<p className="text-xs text-muted-foreground">
						Drafted from your catalogue when the store was set up. Tap one to add it to your widget; prompts show how
						many products smart search found.
					</p>
				</div>
				<button
					className="px-3 py-2 border border-border rounded-md hover:bg-accent transition-colors disabled:opacity-50 flex items-center gap-2 text-sm"
					disabled={!!busy}
					onClick={generate}>
					<RefreshCw className={cn("w-4 h-4", busy === "generate" && "animate-spin")} />
					{busy === "generate" ? "Generating..." : "Generate more"}
				</button>
			</div>
			{!chips.length && !placeholders.length && !prompts.length ? (
				<p className="text-sm text-muted-foreground">No suggestions yet. Use “Generate more” to draft some.</p>
			) : (
				<div className="space-y-2">
					<div className={group}>Quick chips · instant search</div>
					<div className="flex flex-wrap gap-2">
						{chips.map((chip) => (
							<button
								className="px-3 py-1 rounded-full border border-border text-sm hover:bg-accent disabled:opacity-50 flex items-center gap-1"
								disabled={!!busy || isActiveChip(chip)}
								key={chip.label}
								onClick={() => addChip(chip)}
								title={`Searches “${chip.query}”`}>
								{!isActiveChip(chip) && <Plus className="w-3 h-3" />}
								{chip.label}
								{isActiveChip(chip) && <span className="text-xs text-muted-foreground"> · in use</span>}
							</button>
						))}
					</div>
					{prompts.length > 0 && (
						<>
							<div className={cn(group, "pt-2")}>Ask-anything prompts · smart search</div>
							<div className="flex flex-col gap-1">
								{prompts.map((prompt) => (
									<button
										aria-pressed={isActivePrompt(prompt)}
										className={cn(
											"text-left px-3 py-2 rounded border text-sm hover:bg-accent disabled:opacity-50 flex items-center gap-2",
											isActivePrompt(prompt) ? "border-primary bg-primary/5" : "border-border",
										)}
										disabled={!!busy || (!isActivePrompt(prompt) && activePrompts.length >= 12)}
										key={prompt.query}
										onClick={() => togglePrompt(prompt)}
										title={
											isActivePrompt(prompt)
												? "Tap to remove from the widget"
												: "Tap to show as a prompt card"
										}>
										{!isActivePrompt(prompt) && <Plus className="w-3 h-3 shrink-0" />}
										<span className="flex-1">
											<b className="font-medium">{prompt.label}</b> · {prompt.query}
										</span>
										{prompt.matches !== undefined && (
											<span className="text-xs text-muted-foreground shrink-0">
												{prompt.matches >= 10 ? "10+" : prompt.matches} matches
											</span>
										)}
										{isActivePrompt(prompt) && (
											<span className="text-xs text-muted-foreground shrink-0">in use</span>
										)}
									</button>
								))}
							</div>
						</>
					)}
					<div className={cn(group, "pt-2")}>Rotating placeholders</div>
					<div className="flex flex-col gap-1">
						{placeholders.map((text) => (
							<button
								className="text-left px-3 py-2 rounded border border-border text-sm hover:bg-accent disabled:opacity-50 flex items-center gap-2"
								disabled={!!busy || isActivePlaceholder(text) || savedPlaceholders.length >= 10}
								key={text}
								onClick={() => addPlaceholder(text)}>
								{!isActivePlaceholder(text) && <Plus className="w-3 h-3 shrink-0" />}
								{text}
								{isActivePlaceholder(text) && <span className="text-xs text-muted-foreground"> · in use</span>}
							</button>
						))}
					</div>
				</div>
			)}
			{message && <p className={cn("text-sm mt-3", message.error ? "text-red-600" : "text-green-700")}>{message.text}</p>}
		</section>
	)
}
